// Players-tab toolbar — My Agency V2 rebuild (ARTBOARD-SPEC boards 2a/2b
// desktop, 2j mobile chip strip). PRESENTATION-ONLY (Brief Rule 6): every
// control drives the exact same props/handlers as before (server params via
// useAgencyProfile — contract_status, expiring_soon, managing_agent,
// nationality, sort; client-side search). Live contract-status counts from
// the board endpoint stay visible (inside the select options + the 2b
// result-meta line). The 90-day window comes from existing logic
// (EXPIRING_SOON_DAYS, utils/contractRadar.js) — never from the mock
// (Brief Rule 10).
//
// NOTE (documented in PARITY-FILL-PLAYERS.md): the 2a "Position" pill is
// NOT rendered here — the position filter is the shared
// components/common/PositionFilterDropdown (also used by AgentPublicProfile,
// MySquad, club MyPlayers) and stays untouched below the toolbar.
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { EXPIRING_SOON_DAYS } from "../../utils/contractRadar";
import {
  SearchInput,
  ChevronDownIcon,
  CloseIcon,
  ClockIcon,
  SortArrowsIcon,
} from "./v2";

const SEGMENTS = [
  { key: "expired", label: "Expired", countKey: "expired" },
  { key: "expiring", label: "Expiring", countKey: "expiring" },
  { key: "neutral", label: "Under contract", countKey: "neutral" },
  { key: "no_data", label: "No data", countKey: "no_data" },
];

// Filter pill (2a resting / 2b active): resting = white pill + SYM-04
// chevron (gap 6); active = brand-tinted pill (gap 7) + trailing SYM-06
// remove (clears the filter back to ""). The <select> keeps the native
// picker = the existing select behavior, restyled.
const FilterPillSelect = ({
  label,
  value,
  onChange,
  options, // [{ value, label }]
  clearTitle,
  mobile = false,
  dataNoTranslate = false,
}) => {
  const active = value != null && value !== "";
  return (
    <span
      className={[
        "flex-none inline-flex items-center whitespace-nowrap rounded-full border",
        mobile
          ? `${active ? "gap-[7px]" : "gap-[5px]"} px-[13px] py-1.5 text-[12.5px]`
          : `${active ? "gap-[7px]" : "gap-1.5"} px-[13px] py-[7px] text-[13px]`,
        active
          ? "bg-[#EAF3FF] border-[#B9D8FF] text-[#1668D9] font-semibold"
          : "bg-white border-[#DCE3EC] text-[#475569] font-medium hover:border-[#B9C6D6]",
      ].join(" ")}
    >
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-no-translate={dataNoTranslate ? "true" : undefined}
        className={`appearance-none bg-transparent outline-none cursor-pointer ${
          active ? "text-[#1668D9] font-semibold" : "text-[#475569] font-medium"
        } ${mobile ? "max-w-[130px]" : "max-w-[180px]"}`}
      >
        <option value="">{label}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {active ? (
        <button
          type="button"
          onClick={() => onChange("")}
          title={clearTitle}
          aria-label={clearTitle}
          className="flex-none inline-flex border-0 bg-transparent p-0 cursor-pointer"
        >
          <CloseIcon size={12} strokeWidth={2.6} stroke="#1668D9" />
        </button>
      ) : (
        <ChevronDownIcon size={13} />
      )}
    </span>
  );
};

const PlayersToolbar = ({
  counts,
  contractFilter = "",
  onContractFilter,
  expiringSoon = false,
  onExpiringSoon,
  sort,
  onSort,
  search,
  onSearch,
  managingAgentFilter = "",
  onManagingAgent,
  agentOptions = [],
  nationalityFilter = "",
  onNationality,
  nationalityOptions = [],
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Live counts stay attached to the contract-status options (board endpoint
  // counts.total/expired/expiring/neutral/no_data — existing data, must not
  // get lost, Brief Rule 7).
  const contractOptions = SEGMENTS.map((s) => {
    const count = counts ? counts[s.countKey] : undefined;
    return { value: s.key, label: count != null ? `${s.label} (${count})` : s.label };
  });

  const agentSelectOptions = agentOptions.map((a) => ({
    value: String(a.account_id),
    label: a.name,
  }));
  const nationalitySelectOptions = nationalityOptions.map((n) => ({
    value: n.name,
    label: n.name,
  }));

  // Board 2b result-meta line — derived purely from the server counts the
  // board endpoint already returns (no new data).
  const resultMeta =
    expiringSoon && counts ? (
      <div
        className={`flex items-baseline gap-[10px] flex-wrap ${
          isMobile ? "px-4" : "px-6"
        } mt-4 text-[13px] text-[#64748B]`}
      >
        <span>
          <b className="text-[#0F172A] font-semibold">
            {(counts.expired || 0) + (counts.expiring || 0)} of {counts.total ?? 0}
          </b>{" "}
          players: {counts.expired || 0} expired, {counts.expiring || 0} expiring within{" "}
          {EXPIRING_SOON_DAYS} days
        </span>
        <button
          type="button"
          onClick={() => onExpiringSoon(false)}
          className="text-[13px] font-semibold text-[#1668D9] border-0 bg-transparent p-0 cursor-pointer"
        >
          Clear filter
        </button>
      </div>
    ) : null;

  // Expiring-soon quick filter: active state = board 2b's removable filter
  // chip ("Contract: expiring ≤ 90 days", ≤ verbatim, trailing SYM-06),
  // placed directly after search exactly as on 2b. The resting state has no
  // dedicated board — styled per the 2a filter-pill family (see
  // PARITY-FILL-PLAYERS.md); the toggle itself is an existing function
  // (Brief Rule 7) and must stay reachable.
  const expiringControl = expiringSoon ? (
    <span
      className={`flex-none inline-flex items-center gap-[7px] whitespace-nowrap rounded-full border bg-[#EAF3FF] border-[#B9D8FF] text-[#1668D9] font-semibold ${
        isMobile ? "px-[13px] py-1.5 text-[12.5px]" : "px-[13px] py-[7px] text-[13px]"
      }`}
    >
      Contract: expiring ≤ {EXPIRING_SOON_DAYS} days
      <button
        type="button"
        onClick={() => onExpiringSoon(false)}
        title="Clear filter"
        aria-label="Clear filter"
        className="flex-none inline-flex border-0 bg-transparent p-0 cursor-pointer"
      >
        <CloseIcon size={12} strokeWidth={2.6} stroke="#1668D9" />
      </button>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => onExpiringSoon(true)}
      className={`flex-none inline-flex items-center whitespace-nowrap rounded-full border bg-white border-[#DCE3EC] text-[#475569] font-medium hover:border-[#B9C6D6] cursor-pointer ${
        isMobile ? "gap-[5px] px-[13px] py-1.5 text-[12.5px]" : "gap-1.5 px-[13px] py-[7px] text-[13px]"
      }`}
    >
      <ClockIcon size={13} stroke="#64748B" strokeWidth={2.2} />
      Expiring soon
    </button>
  );

  if (isMobile) {
    // Board 2j: full-width search, then the horizontally scrollable chip
    // strip (active sort chip blue with SYM-25 white; filters as chips).
    return (
      <>
        <div className="px-4 pt-3">
          <SearchInput
            value={search}
            onChange={onSearch}
            onClear={() => onSearch("")}
            placeholder="Search players"
            width="100%"
            mobile
            clearTitle="Clear search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto px-4 pt-[13px]">
          {/* Active sort chip (2j): brand-filled, SYM-25 11/2.2 #fff */}
          <span className="flex-none inline-flex items-center gap-[5px] whitespace-nowrap rounded-full bg-[#2283FF] px-[13px] py-1.5 text-[12.5px] font-semibold text-white">
            <SortArrowsIcon size={11} strokeWidth={2.2} stroke="#fff" />
            <select
              value={sort}
              onChange={(e) => onSort(e.target.value)}
              className="appearance-none bg-transparent outline-none cursor-pointer text-white font-semibold"
            >
              <option value="contract_expiring">Expiring soonest</option>
              <option value="name">Name</option>
            </select>
          </span>
          {expiringControl}
          <FilterPillSelect
            mobile
            label="Contract"
            value={contractFilter}
            onChange={onContractFilter}
            options={contractOptions}
            clearTitle="Clear filter"
          />
          <FilterPillSelect
            mobile
            label="Agent"
            value={managingAgentFilter}
            onChange={onManagingAgent}
            options={agentSelectOptions}
            clearTitle="Clear filter"
            dataNoTranslate
          />
          <FilterPillSelect
            mobile
            label="Nationality"
            value={nationalityFilter}
            onChange={onNationality}
            options={nationalitySelectOptions}
            clearTitle="Clear filter"
            dataNoTranslate
          />
        </div>
        {resultMeta}
      </>
    );
  }

  // Boards 2a/2b desktop: search 230px leads, active expiring chip directly
  // after it (2b), then the filter-pill family (2a order: Nationality ·
  // Contract status · Agent — Position sits below, see header note), sort
  // control pinned right (margin-left auto).
  return (
    <>
      <div className="flex items-center gap-[10px] flex-wrap px-6 mt-4">
        <SearchInput
          value={search}
          onChange={onSearch}
          onClear={() => onSearch("")}
          placeholder="Search players"
          width={230}
          clearTitle="Clear search"
        />
        {expiringControl}
        <FilterPillSelect
          label="Nationality"
          value={nationalityFilter}
          onChange={onNationality}
          options={nationalitySelectOptions}
          clearTitle="Clear filter"
          dataNoTranslate
        />
        <FilterPillSelect
          label="Contract status"
          value={contractFilter}
          onChange={onContractFilter}
          options={contractOptions}
          clearTitle="Clear filter"
        />
        <FilterPillSelect
          label="Agent"
          value={managingAgentFilter}
          onChange={onManagingAgent}
          options={agentSelectOptions}
          clearTitle="Clear filter"
          dataNoTranslate
        />
        {/* Sort control (2a): SYM-25 + "Sort:" + value + SYM-04, ml-auto */}
        <label className="ml-auto flex-none inline-flex items-center gap-[7px] whitespace-nowrap rounded-[9px] border border-[#DCE3EC] bg-white px-[13px] py-[7px] text-[13px] text-[#475569] cursor-pointer hover:border-[#B9C6D6]">
          <SortArrowsIcon size={13} />
          <span className="text-[#94A3B8]">Sort:</span>
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value)}
            className="appearance-none bg-transparent outline-none cursor-pointer font-semibold text-[#0F172A]"
          >
            <option value="contract_expiring">Contract expiring soonest</option>
            <option value="name">Name</option>
          </select>
          <ChevronDownIcon size={13} />
        </label>
      </div>
      {resultMeta}
    </>
  );
};

export default PlayersToolbar;
